import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { RefreshCw, Server } from 'lucide-react';
import { DashboardData } from '../../types';
import { usePermissions } from '../../hooks/usePermissions';

interface OutletContext {
	dashboardData: DashboardData | null;
	refreshKey: number;
}

interface SystemComponent {
	name: string;
	status: 'operational' | 'degraded' | 'down';
	description: string;
}

interface SystemStatus {
	status: 'operational' | 'degraded' | 'major_outage';
	message: string;
	timestamp: string;
	components: SystemComponent[];
}

export const SystemStatusPage: React.FC = () => {
	const { dashboardData, refreshKey } = useOutletContext<OutletContext>();
	const { isClientUser, clientName } = usePermissions();

	const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
	const [loading, setLoading] = useState(true);
	const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

	const fetchSystemStatus = async () => {
		try {
			setLoading(true);
			const response = await fetch('/api/health/detailed');
			const data = await response.json();

			const toComponentStatus = (s: string): 'operational' | 'degraded' | 'down' =>
				s === 'healthy' ? 'operational' : s === 'degraded' ? 'degraded' : 'down';

			const components: SystemComponent[] = [
				{ name: 'API', status: toComponentStatus(data.status), description: 'Main API endpoints' },
				{
					name: 'Webhook Processing',
					status: toComponentStatus(data.checks?.queue?.status ?? 'unhealthy'),
					description: 'Real-time event processing',
				},
				{
					name: 'Background Jobs',
					status: toComponentStatus(data.checks?.queue?.status ?? 'unhealthy'),
					description: 'Async sync processing',
				},
				{
					name: 'Database',
					status: toComponentStatus(data.checks?.database?.status ?? 'unhealthy'),
					description: 'Data storage',
				},
				{
					name: 'Cache',
					status: toComponentStatus(data.checks?.redis?.status ?? 'unhealthy'),
					description: 'Redis / job queue',
				},
			];

			const hasDown = components.some((c) => c.status === 'down');
			const hasDegraded = components.some((c) => c.status === 'degraded');

			setSystemStatus({
				status: hasDown ? 'major_outage' : hasDegraded ? 'degraded' : 'operational',
				message: hasDown ? 'System experiencing issues' : hasDegraded ? 'System degraded' : 'All systems operational',
				timestamp: data.timestamp,
				components,
			});
			setLastUpdated(new Date());
		} catch (error) {
			console.error('Failed to fetch system status:', error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (refreshKey > 0) {
			fetchSystemStatus();
		}
	}, [refreshKey]);

	useEffect(() => {
		fetchSystemStatus();
		// Auto-refresh every 30 seconds
		const interval = setInterval(fetchSystemStatus, 30000);
		return () => clearInterval(interval);
	}, []);

	const getStatusColor = (status: string) => {
		switch (status) {
			case 'operational':
				return 'bg-green-50 border-green-200 text-green-800';
			case 'degraded':
				return 'bg-yellow-50 border-yellow-200 text-yellow-800';
			case 'down':
			case 'major_outage':
				return 'bg-red-50 border-red-200 text-red-800';
			default:
				return 'bg-gray-50 border-gray-200 text-gray-800';
		}
	};

	const getStatusBadgeColor = (status: string) => {
		switch (status) {
			case 'operational':
				return 'bg-green-100 text-green-800';
			case 'degraded':
				return 'bg-yellow-100 text-yellow-800';
			case 'down':
				return 'bg-red-100 text-red-800';
			default:
				return 'bg-gray-100 text-gray-800';
		}
	};

	const getStatusIcon = (status: string) => {
		switch (status) {
			case 'operational':
				return '✓';
			case 'degraded':
				return '!!';
			case 'down':
				return '✗';
			default:
				return '?';
		}
	};

	const getStatusText = (status: string) => {
		switch (status) {
			case 'operational':
				return 'Operational';
			case 'degraded':
				return 'Degraded';
			case 'down':
				return 'Down';
			default:
				return 'Unknown';
		}
	};

	if (!dashboardData) {
		return (
			<div className="flex items-center justify-center min-h-[400px]">
				<div className="text-center">
					<RefreshCw className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" />
					<p className="text-sm text-gray-600">Loading dashboard...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* CLIENT CONTEXT BANNER - Show for CLIENT_USERs */}
			{isClientUser && clientName && (
				<div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
					<div className="flex items-center space-x-3">
						<div className="flex-shrink-0">
							<div className="h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center">
								<Server className="h-6 w-6 text-white" />
							</div>
						</div>
						<div className="flex-1">
							<h3 className="text-lg font-semibold text-gray-900">System Status</h3>
							<p className="text-sm text-gray-600">Real-time health monitoring for all integration components</p>
						</div>
					</div>
				</div>
			)}

			{/* Overall Status Banner */}
			{systemStatus && (
				<div className={`rounded-xl border-2 p-6 ${getStatusColor(systemStatus.status)}`}>
					<div className="flex items-center justify-between">
						<div>
							<h2 className="text-2xl font-bold mb-2">{systemStatus.message}</h2>
							<p className="text-sm opacity-75">Last updated: {lastUpdated.toLocaleString()}</p>
						</div>
						<button
							onClick={fetchSystemStatus}
							disabled={loading}
							className="p-2 hover:bg-white/50 rounded-lg transition-colors"
							title="Refresh status">
							<RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
						</button>
					</div>
				</div>
			)}

			{/* System Components */}
			<div className="bg-white rounded-xl border border-gray-200">
				<div className="px-6 py-4 border-b border-gray-200">
					<div className="flex items-center justify-between">
						<div>
							<h3 className="text-lg font-semibold text-gray-900">System Components</h3>
							<p className="text-sm text-gray-500 mt-1">Health status of all integration components</p>
						</div>
						<div className="text-sm text-gray-500">Auto-refreshes every 30 seconds</div>
					</div>
				</div>

				<div className="divide-y divide-gray-200">
					{loading && !systemStatus ? (
						<div className="p-8 text-center">
							<RefreshCw className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" />
							<p className="text-sm text-gray-600">Loading system status...</p>
						</div>
					) : systemStatus?.components.length === 0 ? (
						<div className="p-8 text-center text-gray-500">
							<Server className="h-12 w-12 mx-auto mb-3 text-gray-400" />
							<p>No system components available</p>
						</div>
					) : (
						systemStatus?.components.map((component, index) => (
							<div key={index} className="px-6 py-4 hover:bg-gray-50 transition-colors">
								<div className="flex items-center justify-between">
									<div className="flex-1">
										<h4 className="text-base font-medium text-gray-900">{component.name}</h4>
										<p className="text-sm text-gray-500 mt-1">{component.description}</p>
									</div>
									<span
										className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold ${getStatusBadgeColor(component.status)}`}>
										<span className="mr-1">{getStatusIcon(component.status)}</span>
										{getStatusText(component.status)}
									</span>
								</div>
							</div>
						))
					)}
				</div>
			</div>
		</div>
	);
};
